import * as React from 'react';
import { toast } from 'sonner';
import type { PhoneCallView } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import { showDesktopNotification } from './desktopNotifications';
import {
	advanceBaseline,
	type AlertBaseline,
	buildHighScoreAlert,
	createBaseline,
	findNewHighScoreCalls,
	type HighScoreAlert,
} from './highScoreAlerts';

const TOAST_DURATION_MS = 15_000;

export interface AlertDelivery {
	desktopEnabled: boolean;
	onOpen?: () => void;
}

/**
 * A hidden tab gets the OS notification (the whole point is to pull him back); a visible one gets the
 * toast, which carries an Open action the notification can't. A blocked or unsupported notification
 * falls through to the toast rather than vanishing.
 */
export function deliverHighScoreAlert(alert: HighScoreAlert, { desktopEnabled, onOpen }: AlertDelivery) {
	const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
	if (hidden && desktopEnabled) {
		const shown = showDesktopNotification({
			title: alert.title,
			body: alert.body,
			tag: alert.tag,
			onClick: onOpen,
		});
		if (shown) {
			return;
		}
	}

	toast.success(alert.title, {
		description: alert.body,
		duration: TOAST_DURATION_MS,
		...(onOpen ? { action: { label: 'Open', onClick: onOpen } } : {}),
	});
}

export interface UseHighScoreCallAlertsOptions {
	enabled?: boolean;
	calls: PhoneCallView[] | undefined;
	isSuccess: boolean;
	repName: string;
	threshold: number;
	desktopEnabled: boolean;
	/** Identity of the question being asked; a different one re-baselines instead of alerting. */
	baselineKey: string;
	onOpenCall: (aiConversationId: string) => void;
}

export function useHighScoreCallAlerts({
	enabled = true,
	calls,
	isSuccess,
	repName,
	threshold,
	desktopEnabled,
	baselineKey,
	onOpenCall,
}: UseHighScoreCallAlertsOptions) {
	const onOpenCallRef = React.useRef(onOpenCall);
	React.useEffect(() => {
		onOpenCallRef.current = onOpenCall;
	}, [onOpenCall]);

	const baselineRef = React.useRef<{ key: string; baseline: AlertBaseline } | null>(null);

	React.useEffect(() => {
		if (!enabled || !isSuccess || !calls) {
			return;
		}

		const known = baselineRef.current;
		if (!known || known.key !== baselineKey) {
			baselineRef.current = { key: baselineKey, baseline: createBaseline(calls) };
			return;
		}

		const fresh = findNewHighScoreCalls(calls, known.baseline, threshold);
		baselineRef.current = { key: baselineKey, baseline: advanceBaseline(known.baseline, calls) };

		const alert = buildHighScoreAlert(repName, fresh, threshold);
		if (!alert) {
			return;
		}

		const { conversationId } = alert;
		deliverHighScoreAlert(alert, {
			desktopEnabled,
			onOpen: conversationId ? () => onOpenCallRef.current(conversationId) : undefined,
		});
	}, [enabled, calls, isSuccess, baselineKey, threshold, repName, desktopEnabled]);
}
